// src/desktop-app/ui/footnote-rendering.js
import { createHierarchicalHeader as createHierarchicalHeaderShared, refreshAllHeadingStats, updateDocumentStats } from '../../common/ui/heading-groups.js';

// Re-export the shared function with the original signature for backward compatibility
export function createHierarchicalHeader(heading, groupItems, level, parentContent, mgr, footnoteListEl) {
  return createHierarchicalHeaderShared(heading, groupItems, level, parentContent, {
    mgr,
    footnoteListEl
  });
}

// Helper function to re-render footnote list with merged inline citations
export function rerenderFootnoteList(mgr, footnoteListEl) {
  console.log('[RERENDER DEBUG] Starting rerender, items:', mgr.items?.length, 'headings:', mgr.headings?.length);
  
  footnoteListEl.innerHTML = '';
  
  const headingMap = Object.fromEntries(mgr.headings.map(h => [h.id, h]));
  const noHeadingItems = [];
  const level1Headings = [];
  const level2Headings = [];
  const level3Headings = [];
  const level4Headings = [];
  
  // Normalize items with missing/invalid heading_id to nearest heading by page
  const byPage = (a, b) => (a.page || 0) - (b.page || 0);
  const headingsByPage = [...(mgr.headings || [])].sort(byPage);
  for (const item of (mgr.items || [])) {
    if (item.is_inline) continue;
    const hasHeading = item.heading_id && headingMap[item.heading_id];
    if (hasHeading) continue;
    const itemPage = (item.page_numbers && item.page_numbers[0])
      || Number((/^p(\d+)-/.exec(item.reference_id) || [])[1]) || 0;
    let nearest = null;
    for (const h of headingsByPage) {
      if ((h.page || 0) <= itemPage) {
        nearest = h;
      } else {
        break;
      }
    }
    if (nearest) {
      item.heading_id = nearest.id;
    }
  }
  
  // Separate items with no heading
  for (const item of (mgr.items || [])) {
    if (!item.heading_id) {
      noHeadingItems.push(item);
    }
  }
  
  console.log('[RERENDER DEBUG] No-heading items:', noHeadingItems.length);
  
  // Group headings by level
  for (const heading of (mgr.headings || [])) {
    const level = heading.level || 1;
    switch (level) {
      case 1: level1Headings.push(heading); break;
      case 2: level2Headings.push(heading); break;
      case 3: level3Headings.push(heading); break;
      case 4: level4Headings.push(heading); break;
      default: level1Headings.push(heading);
    }
  }

  // Keep headings ordered by page so nested ranges are stable
  level1Headings.sort(byPage);
  level2Headings.sort(byPage);
  level3Headings.sort(byPage);
  level4Headings.sort(byPage);
  
  console.log('[RERENDER DEBUG] Level 1 headings:', level1Headings.map(h => `${h.id} (level ${h.level || 1})`));
  console.log('[RERENDER DEBUG] All headings in map:', Object.keys(headingMap));
  
  // Group items by heading
  const byHeading = {};
  for (const item of (mgr.items || [])) {
    if (item.heading_id) {
      (byHeading[item.heading_id] ||= []).push(item);
    }
  }
  
  console.log('[RERENDER DEBUG] Items by heading:', Object.keys(byHeading).length, 'headings with items');
  console.log('[RERENDER DEBUG] Heading breakdown:', Object.entries(byHeading).map(([h, items]) => `${h}: ${items.length}`));
  
  // Check if headings with items exist in headingMap
  for (const headingId of Object.keys(byHeading)) {
    if (!headingMap[headingId]) {
      console.warn(`[RERENDER DEBUG] ⚠️ Heading ${headingId} has items but doesn't exist in headingMap!`);
    } else {
      const heading = headingMap[headingId];
      const isLevel1 = level1Headings.some(h => h.id === headingId);
      console.log(`[RERENDER DEBUG] Heading ${headingId}: exists, level=${heading.level || 1}, isLevel1=${isLevel1}`);
    }
  }
  
  // Check if we have any items at all (including inline citations)
  const totalItems = (mgr.items || []).length;
  const hasInlineCitations = (mgr.items || []).some(item => item.is_inline === true);
  
  console.log('[RERENDER DEBUG] Total items:', totalItems, 'Has inline citations:', hasInlineCitations);
  
  if (!level1Headings.length && !noHeadingItems.length && totalItems === 0) {
    footnoteListEl.innerHTML = `<div class="empty-state">✅ Process complete. No footnotes were found.</div>`;
    return; // Early return if truly empty
  }
  
  // Render headings in document order using a level stack
  const headingsSorted = (mgr.headings || [])
    .map((h, idx) => ({ ...h, __idx: idx }))
    .sort((a, b) => byPage(a, b) || (a.__idx - b.__idx));
  const parentStack = []; // { level, contentEl }
  for (const heading of headingsSorted) {
    const level = heading.level || 1;
    while (parentStack.length > 0 && parentStack[parentStack.length - 1].level >= level) {
      parentStack.pop();
    }
    const parentContent = parentStack.length > 0 ? parentStack[parentStack.length - 1].contentEl : null;
    const groupItems = byHeading[heading.id] || [];
    const { headerEl, contentEl } = createHierarchicalHeader(
      heading, groupItems, level, parentContent, mgr, footnoteListEl
    );
    if (parentContent) {
      parentContent.appendChild(headerEl);
      parentContent.appendChild(contentEl);
    } else {
      footnoteListEl.appendChild(headerEl);
      footnoteListEl.appendChild(contentEl);
    }
    parentStack.push({ level, contentEl });
  }
  
  // Render no-heading items (including inline citations with no heading)
  if (noHeadingItems.length > 0) {
    const { headerEl, contentEl } = createHierarchicalHeader(
      null, noHeadingItems, 0, null, mgr, footnoteListEl
    );
    footnoteListEl.appendChild(headerEl);
    footnoteListEl.appendChild(contentEl);
  }
  
  // No additional unrendered-item handling needed; heading normalization covers it
  
  // Auto-render cached results for all footnotes after rendering
  // (createHierarchicalHeader already handles this, but we do it here too for safety)
  const allCards = footnoteListEl.querySelectorAll('.card');
  for (const cardEl of allCards) {
    const footnoteId = cardEl.dataset.id;
    const item = mgr.items.find(it => String(it.id) === footnoteId);
    
    // Check resultsCache first - it has the most up-to-date results
    const cachedResults = mgr?.resultsCache?.getResults?.(footnoteId);
    // A reopened document can have results split between the item payload and
    // ResultsCache (for example, one source contains the resolved citations and
    // the other contains the remaining persisted results). Merge them so a
    // partial cache never hides the other source.
    const itemResults = item?.cached_results && typeof item.cached_results === 'object'
      ? item.cached_results
      : {};
    const resultsToRender = (cachedResults || itemResults) && {
      ...itemResults,
      ...(cachedResults && typeof cachedResults === 'object' ? cachedResults : {}),
    };
    
    if (resultsToRender && Object.keys(resultsToRender).length > 0) {
      const resultsContainer = cardEl.querySelector('.results');
      if (resultsContainer) {
        // Render each cached citation into its matching block. The container
        // already contains placeholder blocks for unresolved citations, so a
        // container-level "already rendered" check drops valid partial results.
        import('../../common/ui/citation-results.js').then(({ renderAuditResults }) => {
          // Extract filename from path (show only filename, not full path)
          const resolveFileDisplay = (data, cid) => {
            const fileId = data?.file_id || data?.file;
            const filePath = data?.file || data?.file_path || data?.resolve_result?.file_path;
            let label = '—';
            
            if (filePath) {
              // Extract just the filename (last part of path)
              const filename = filePath.split(/[\\/]/).pop();
              label = filename || filePath;
            } else if (fileId && mgr?.fileCatalog && (data?.source_db || data?.resolve_result?.source_db || 'doc') === 'doc') {
              // Try to get path from file catalog (document's own DB only)
              const fileInfo = mgr.fileCatalog.get(String(fileId));
              if (fileInfo?.path) {
                const filename = fileInfo.path.split(/[\\/]/).pop();
                label = filename || fileInfo.path;
              } else if (fileInfo?.title) {
                label = fileInfo.title;
              } else {
                label = `File ${fileId}`;
              }
            } else if (fileId) {
              label = `File ${fileId}`;
            }
            
            return { fileId: fileId ? String(fileId) : null, label };
          };
          
          renderAuditResults(footnoteListEl.closest('#footnote-list') || document.body, footnoteId, resultsToRender, {
            resolveFileDisplay,
            onChangeFile: (citeId) => {
              if (mgr?.reassignCitationFile) {
                mgr.reassignCitationFile(footnoteId, citeId);
              }
            }
          });
          // Cached result blocks are added asynchronously. Re-apply citation
          // visibility now so hidden subtypes do not flash back into view when
          // a verified document is reopened.
          mgr?.applyCitationTypeVisibility?.();
          
          // Update button text to "Check Again" after rendering cached results
          const verifyBtn = cardEl.querySelector('.card-body .row button');
          if (verifyBtn) {
            verifyBtn.textContent = 'Check Again';
          }
        }).catch(err => {
          console.error('[AUTO-RENDER] Failed to render cached results:', err);
        });
      }
    }
  }

  // Refresh all heading stats after rendering (including recursive aggregation)
  // Use requestAnimationFrame + setTimeout to ensure all async rendering is complete
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (mgr?.resultsCache) {
          refreshAllHeadingStats(footnoteListEl, mgr.resultsCache, mgr.items || []);
        }
      }, 150);
    });
  });
}

